<script lang="ts">
    import { Link, useForm, page } from '@inertiajs/svelte';
    import SocialLoginButtons from '../components/SocialLoginButtons.svelte';
    import { login } from '@/routes';
    import logo from '../../images/logo.png';

    const turnstileSiteKey: string | null = $derived($page?.props?.turnstileSiteKey as string | null);
    const showCaptcha = $derived(!!turnstileSiteKey);

    const form = useForm({
        name: '',
        email: '',
        password: '',
        password_confirmation: '',
        gdpr_consent: false,
        cf_turnstile_response: '',
    });

    const errors = $derived($page?.props?.errors ?? {});

    let turnstileContainer: HTMLDivElement | undefined = $state();
    let turnstileWidgetId: string | undefined = $state();

    function renderTurnstile(): void {
        if (!turnstileContainer || !turnstileSiteKey || !window.turnstile) {
            return;
        }
        if (turnstileWidgetId !== undefined) {
            window.turnstile.reset(turnstileWidgetId);
            return;
        }
        turnstileWidgetId = window.turnstile.render(turnstileContainer, {
            sitekey: turnstileSiteKey,
            callback: (token: string) => { $form.cf_turnstile_response = token; },
            'expired-callback': () => { $form.cf_turnstile_response = ''; },
            'error-callback': () => { $form.cf_turnstile_response = ''; },
        });
    }

    $effect(() => {
        if (!showCaptcha || !turnstileContainer) {
            return;
        }

        if (window.turnstile) {
            renderTurnstile();
            return;
        }

        const script = document.createElement('script');
        script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit&onload=onTurnstileLoad';
        script.async = true;
        (window as any).onTurnstileLoad = renderTurnstile;
        document.head.appendChild(script);

        return () => {
            delete (window as any).onTurnstileLoad;
        };
    });

    function submit(e: SubmitEvent) {
        e.preventDefault();
        $form.post('/register');
    }
</script>

<svelte:head>
    <title>Register — Case of the Day</title>
</svelte:head>

<div class="min-h-screen flex flex-col items-center justify-center bg-gray-950">
    <img src={logo} alt="Case of the Day" class="h-24 w-24 mb-6" />
    <div class="bg-gray-900 rounded-lg shadow border border-gray-700 p-8 w-full max-w-lg">
        <h1 class="text-2xl font-bold text-gray-100 mb-6 text-center">Create Account</h1>

        <SocialLoginButtons mode="signup" />

        <form onsubmit={submit} class="space-y-4">
            <div>
                <label class="block text-sm font-medium text-gray-300 mb-1">Name</label>
                <input
                    bind:value={$form.name}
                    type="text"
                    autocomplete="name"
                    required
                    class="w-full border rounded-md px-3 py-2 text-sm bg-gray-800 text-gray-100 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-colors {errors.name ? 'border-red-500' : 'border-gray-700'}"
                />
                {#if errors.name}<p class="text-red-500 text-sm mt-1">{errors.name}</p>{/if}
            </div>
            <div>
                <label class="block text-sm font-medium text-gray-300 mb-1">Email</label>
                <input
                    bind:value={$form.email}
                    type="email"
                    autocomplete="email"
                    required
                    class="w-full border rounded-md px-3 py-2 text-sm bg-gray-800 text-gray-100 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-colors {errors.email ? 'border-red-500' : 'border-gray-700'}"
                />
                {#if errors.email}<p class="text-red-500 text-sm mt-1">{errors.email}</p>{/if}
            </div>
            <div>
                <label class="block text-sm font-medium text-gray-300 mb-1">Password</label>
                <input
                    bind:value={$form.password}
                    type="password"
                    autocomplete="new-password"
                    required
                    class="w-full border rounded-md px-3 py-2 text-sm bg-gray-800 text-gray-100 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-colors {errors.password ? 'border-red-500' : 'border-gray-700'}"
                />
                {#if errors.password}<p class="text-red-500 text-sm mt-1">{errors.password}</p>{/if}
            </div>
            <div>
                <label class="block text-sm font-medium text-gray-300 mb-1">Confirm Password</label>
                <input
                    bind:value={$form.password_confirmation}
                    type="password"
                    autocomplete="new-password"
                    required
                    class="w-full border rounded-md px-3 py-2 text-sm bg-gray-800 text-gray-100 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-colors {errors.password_confirmation ? 'border-red-500' : 'border-gray-700'}"
                />
                {#if errors.password_confirmation}<p class="text-red-500 text-sm mt-1">{errors.password_confirmation}</p>{/if}
            </div>
            <div>
                <div class="flex items-start gap-2">
                    <input
                        bind:checked={$form.gdpr_consent}
                        type="checkbox"
                        id="gdpr"
                        class="mt-1 rounded border-gray-700 text-cyan-600 focus:ring-cyan-500 bg-gray-800"
                    />
                    <label for="gdpr" class="text-sm {errors.gdpr_consent ? 'text-red-600' : 'text-gray-400'}">
                        I consent to the storage and processing of my personal data.
                    </label>
                </div>
                {#if errors.gdpr_consent}<p class="text-red-500 text-sm mt-1">{errors.gdpr_consent}</p>{/if}
            </div>
            {#if showCaptcha}
                <div>
                    <div bind:this={turnstileContainer}></div>
                    {#if errors.cf_turnstile_response}
                        <p class="text-red-500 text-sm mt-1">{errors.cf_turnstile_response}</p>
                    {/if}
                </div>
            {/if}
            <button type="submit" disabled={$form.processing} class="w-full bg-cyan-600 text-white py-2 rounded-md text-sm font-medium hover:bg-cyan-500 disabled:opacity-50 transition-colors">
                Create Account
            </button>
        </form>

        <p class="mt-4 text-center text-sm text-gray-400">
            Already have an account? <Link href={login()} class="text-cyan-400 hover:text-cyan-300 hover:underline">Sign in</Link>
        </p>
    </div>
</div>
